package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.ZIndex
import co.maytes.standupbot.shared.dto.model.ModelDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunDetailDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import kotlin.math.max
import kotlin.math.roundToLong

private const val MIN_ESTIMATED_CENTS = 1

val AVAILABLE_MODELS =
    listOf(
        ModelDto("claude-opus-5-5", "Claude Opus 5.5", inputPerMillion = 4.00, outputPerMillion = 20.00, default = false),
        ModelDto("claude-sonnet-5-5", "Claude Sonnet 5.5", inputPerMillion = 2.00, outputPerMillion = 10.00, default = true),
        ModelDto("claude-haiku-5-5", "Claude Haiku 5.5", inputPerMillion = 0.10, outputPerMillion = 0.50, default = false),
    )

@Composable
fun RegenerateStandupModal(
    current: StandupRunDetailDto,
    models: List<ModelDto> = AVAILABLE_MODELS,
    submitting: Boolean = false,
    onDismiss: () -> Unit,
    onConfirm: (model: String) -> Unit,
) {
    val effectiveModels = models.ifEmpty { AVAILABLE_MODELS }
    val defaultId = current.currentRevision?.aiModel
        ?.takeIf { id -> effectiveModels.any { it.id == id } }
        ?: effectiveModels.firstOrNull { it.default }?.id
        ?: effectiveModels.first().id
    var selected by remember(effectiveModels) { mutableStateOf(defaultId) }

    val estimatedCents = remember(current, selected, effectiveModels) {
        val model = effectiveModels.firstOrNull { it.id == selected } ?: return@remember null
        val input = current.aiInputTokens ?: return@remember null
        val output = current.aiOutputTokens ?: return@remember null
        estimateCents(model, input, output)
    }

    Box(
        modifier = Modifier
            .styleModifier {
                property("position", "fixed")
                property("inset", "0")
                property("background-color", "rgba(9, 9, 11, 0.55)")
                property("backdrop-filter", "blur(4px)")
                property("z-index", "${ZIndex.modal}")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
            .onClick { onDismiss() },
    ) {
        Column(
            modifier = Modifier
                .backgroundColor(Palette.surface)
                .borderRadius(Radius.lg)
                .padding(Spacing.lg)
                .width(480.px)
                .styleModifier {
                    property("max-width", "calc(100vw - 48px)")
                    property("border", "1px solid ${Palette.border.cssRgb()}")
                    property("box-shadow", Shadow.LG)
                }
                .onClick { evt -> evt.stopPropagation() },
        ) {
            SpanText(
                text = "Regenerate standup",
                modifier = Modifier
                    .fontSize(Typography.h2.size)
                    .fontWeight(Typography.h2.weight)
                    .color(Palette.textPrimary)
                    .margin(bottom = Spacing.sm),
            )
            SpanText(
                text =
                    "Pick a Claude model to resynthesize this standup. The new revision becomes the current " +
                        "one, but Slack won't be re-posted.",
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textSecondary)
                    .margin(bottom = Spacing.md),
            )

            SpanText(
                text = "Model",
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .fontWeight(Typography.caption.weight)
                    .color(Palette.textTertiary)
                    .margin(bottom = Spacing.xxs)
                    .styleModifier {
                        property("text-transform", "none")
                        property("letter-spacing", "0.04em")
                    },
            )
            Select(
                value = selected,
                options = effectiveModels.map { SelectOption(it.id, it.label) },
                onValueChange = { selected = it },
                modifier = Modifier.width(100.percent),
            )

            SpanText(
                text = estimatedCents?.let { "Estimated cost: ~${formatCents(it)}" } ?: "Estimated cost: —",
                modifier = Modifier
                    .margin(top = Spacing.sm)
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textSecondary),
            )

            Row(
                modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Row(horizontalArrangement = Arrangement.spacedBy(Spacing.sm)) {
                    Button(
                        label = "Cancel",
                        variant = ButtonVariant.Ghost,
                        enabled = !submitting,
                        onClick = onDismiss,
                    )
                    Button(
                        label = if (submitting) "Regenerating…" else "Regenerate",
                        variant = ButtonVariant.Primary,
                        enabled = !submitting,
                        onClick = { onConfirm(selected) },
                    )
                }
            }
        }
    }
}

private fun estimateCents(
    model: ModelDto,
    inputTokens: Int,
    outputTokens: Int,
): Int {
    val inputUsd = inputTokens.toDouble() / 1_000_000.0 * model.inputPerMillion
    val outputUsd = outputTokens.toDouble() / 1_000_000.0 * model.outputPerMillion
    val totalUsd = inputUsd + outputUsd
    val cents = (totalUsd * 100.0).roundToLong()
    return max(cents.toInt(), MIN_ESTIMATED_CENTS)
}
